import React, { useState, useMemo } from 'react';
import {
  Button,
  Input,
  Select,
  Space,
  Tag,
  Typography,
  List,
  Modal,
  Form,
  InputNumber,
  Spin,
  Empty,
  App,
  Tooltip,
  Switch,
} from 'antd';
import {
  DisconnectOutlined,
  PlusCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  useGetProductsWithStockQuery,
  useUnlinkProductFromInventoryMutation,
} from '../../services/productsApi';
import {
  useAdjustStockMutation,
} from '../../services/inventoryApi';
import { ProductStock } from '../../types/products';
import { AdjustStockRequest } from '../../types/inventory';
import { formatStock } from '../../utils/unitHelpers';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const normalizeText = (text: string) =>
  text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const KitchenStockDrawerTab: React.FC = () => {
  const { message: messageApi, modal } = App.useApp();

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [linkTypeFilter, setLinkTypeFilter] = useState<string | undefined>(undefined);

  // Stock adjust modal
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [adjustingProduct, setAdjustingProduct] = useState<ProductStock | null>(null);
  const [stockForm] = Form.useForm();

  // API hooks
  const { data: products = [], isLoading } = useGetProductsWithStockQuery();
  const [adjustStock, { isLoading: adjustingStock }] = useAdjustStockMutation();
  const [unlinkProduct] = useUnlinkProductFromInventoryMutation();

  // Filtered products — only show inventory-linked items (direct or recipe)
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (p.inventoryLinkType === 'none') return false;
      const matchesSearch =
        !searchTerm ||
        normalizeText(p.productName).includes(normalizeText(searchTerm)) ||
        normalizeText(p.categoryName).includes(normalizeText(searchTerm));
      const matchesLinkType = !linkTypeFilter || p.inventoryLinkType === linkTypeFilter;
      return matchesSearch && matchesLinkType;
    });
  }, [products, searchTerm, linkTypeFilter]);

  // Handlers
  const handleOpenStockModal = (product: ProductStock) => {
    setAdjustingProduct(product);
    stockForm.resetFields();
    stockForm.setFieldsValue({ quantity: 0, notes: '' });
    setStockModalOpen(true);
  };

  const handleAdjustStock = async () => {
    if (!adjustingProduct?.inventoryItemId) return;
    try {
      const values = await stockForm.validateFields();
      const data: AdjustStockRequest = { quantity: values.quantity, notes: values.notes || undefined };
      await adjustStock({ id: adjustingProduct.inventoryItemId, data }).unwrap();
      messageApi.success('Stock ajustado');
      setStockModalOpen(false);
      setAdjustingProduct(null);
      stockForm.resetFields();
    } catch (error: any) {
      if (error?.errorFields) return;
      messageApi.error(error?.data?.message || 'Error al ajustar stock');
    }
  };

  const handleZeroStock = async (product: ProductStock) => {
    if (!product.inventoryItemId || !product.currentStock) return;
    try {
      const data: AdjustStockRequest = { quantity: -product.currentStock, notes: 'Vaciado desde cocina' };
      await adjustStock({ id: product.inventoryItemId, data }).unwrap();
      messageApi.success(`${product.productName}: stock en cero`);
    } catch (error: any) {
      messageApi.error(error?.data?.message || 'Error al vaciar stock');
    }
  };

  const handleToggleStock = (checked: boolean, product: ProductStock) => {
    if (checked) {
      // Turning ON → open modal to add stock
      handleOpenStockModal(product);
    } else {
      // Turning OFF → zero out stock
      handleZeroStock(product);
    }
  };

  const handleUnlinkProduct = (product: ProductStock) => {
    modal.confirm({
      title: 'Desvincular del inventario',
      content: `¿Desvincular "${product.productName}"? El insumo seguira en inventario.`,
      okText: 'Desvincular',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await unlinkProduct(product.productId).unwrap();
          messageApi.success('Desvinculado');
        } catch (error: any) {
          messageApi.error(error?.data?.message || 'Error al desvincular');
        }
      },
    });
  };

  // Render helpers
  const renderLinkTag = (type: string) => {
    switch (type) {
      case 'direct': return <Tag color="blue" style={{ fontSize: 11 }}>Insumo</Tag>;
      case 'recipe': return <Tag color="purple" style={{ fontSize: 11 }}>Receta</Tag>;
      default: return null;
    }
  };

  const renderStockTag = (product: ProductStock) => {
    if (product.inventoryLinkType === 'direct') {
      return (
        <Tag color={product.isLowStock ? 'red' : 'green'} style={{ fontSize: 11 }}>
          {formatStock(product.currentStock ?? 0)} {product.unit}
        </Tag>
      );
    }
    if (product.inventoryLinkType === 'recipe') {
      return (
        <Tag color={product.hasSufficientStock ? 'green' : 'orange'} style={{ fontSize: 11 }}>
          {product.ingredientCount} ing.
        </Tag>
      );
    }
    return null;
  };

  return (
    <div>
      {/* Filters */}
      <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
        <Input
          placeholder="Buscar insumo..."
          prefix={<SearchOutlined />}
          allowClear
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1 }}
          size="small"
        />
        <Select
          placeholder="Tipo"
          allowClear
          value={linkTypeFilter}
          onChange={(v) => setLinkTypeFilter(v)}
          style={{ width: 120 }}
          size="small"
        >
          <Option value="direct">Insumo</Option>
          <Option value="recipe">Receta</Option>
        </Select>
      </div>

      {/* Inventory-linked product list */}
      {isLoading ? (
        <Spin />
      ) : filteredProducts.length === 0 ? (
        <Empty description="No hay insumos vinculados" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <List<ProductStock>
          dataSource={filteredProducts}
          style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}
          renderItem={(product) => (
            <List.Item
              style={{
                marginBottom: 4,
                padding: '8px 12px',
                borderRadius: 4,
              }}
              actions={product.inventoryLinkType === 'direct' ? [
                <Tooltip key="stock-switch" title={product.currentStock && product.currentStock > 0 ? 'Poner en cero' : 'Agregar stock'}>
                  <Switch
                    checked={(product.currentStock ?? 0) > 0}
                    onChange={(checked) => handleToggleStock(checked, product)}
                    checkedChildren="Hay"
                    unCheckedChildren="0"
                    size="small"
                  />
                </Tooltip>,
              ] : undefined}
            >
              <List.Item.Meta
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                    <Text strong style={{ fontSize: 13 }}>
                      {product.productName}
                    </Text>
                    {renderLinkTag(product.inventoryLinkType)}
                    {renderStockTag(product)}
                  </div>
                }
                description={
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {product.categoryName}
                    </Text>
                    {product.inventoryLinkType === 'direct' && (
                      <Space size={2}>
                        <Tooltip title="Ajustar stock">
                          <Button size="small" type="text" icon={<PlusCircleOutlined />} onClick={() => handleOpenStockModal(product)} style={{ padding: '0 4px' }} />
                        </Tooltip>
                        <Tooltip title="Desvincular">
                          <Button size="small" type="text" danger icon={<DisconnectOutlined />} onClick={() => handleUnlinkProduct(product)} style={{ padding: '0 4px' }} />
                        </Tooltip>
                      </Space>
                    )}
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}

      {/* Stock Adjustment Modal */}
      <Modal
        title={`Ajustar Stock - ${adjustingProduct?.productName || ''}`}
        open={stockModalOpen}
        onCancel={() => { setStockModalOpen(false); setAdjustingProduct(null); stockForm.resetFields(); }}
        onOk={handleAdjustStock}
        confirmLoading={adjustingStock}
        okText="Ajustar"
        cancelText="Cancelar"
        width="98%"
        destroyOnClose
      >
        {adjustingProduct && (
          <div style={{ marginBottom: 12, padding: 8, backgroundColor: 'var(--bg-secondary)', borderRadius: 8 }}>
            <Text type="secondary">Stock actual: </Text>
            <Tag color={adjustingProduct.isLowStock ? 'red' : 'green'}>
              {formatStock(adjustingProduct.currentStock ?? 0)} {adjustingProduct.unit}
            </Tag>
          </div>
        )}
        <Form form={stockForm} layout="vertical" initialValues={{ quantity: 0, notes: '' }}>
          <Form.Item
            name="quantity"
            label="Cantidad (+agregar / -restar)"
            rules={[
              { required: true, message: 'Requerido' },
              { validator: (_, v) => v === 0 ? Promise.reject('No puede ser cero') : Promise.resolve() },
            ]}
          >
            <InputNumber style={{ width: '100%' }} precision={2} placeholder="Ej: 10 o -5" />
          </Form.Item>
          <Form.Item name="notes" label="Notas">
            <TextArea rows={2} placeholder="Motivo (opcional)" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default KitchenStockDrawerTab;
